/**
 * Commission clawback on a defaulted deal (#27 D, Josh 09/29).
 *   not clawed back  → "Claw back commission" (any time — "allow the option to clawback at a later time"),
 *                      with the business-days-since-funding context
 *   clawed back      → status requested / recovered / waived; Mark recovered (date, amount, ref), Waive, Re-open
 * Reads /deals/{id}/default-preview (clawback block); writes through /commissions/clawback and
 * /commissions/clawback/recovery. The commission itself is never a ledger row here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Undo2 } from 'lucide-react';
import { formatCurrency, formatDate } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BADGE = { requested: 'destructive', recovered: 'secondary', waived: 'outline' };

const ClawbackCard = ({ deal, getAuthHeaders, onChanged }) => {
  const [info, setInfo] = useState(null);
  const [dlg, setDlg] = useState(null);   // 'claw' | 'recovered'
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [rec, setRec] = useState({ date: new Date().toISOString().slice(0, 10), amount: '', reference: '', note: '' });

  const load = useCallback(async () => {
    if (!deal?.id) return;
    try {
      const r = await axios.get(`${API}/deals/${deal.id}/default-preview`, { headers: getAuthHeaders() });
      setInfo(r.data.clawback || null);
    } catch (e) { /* card just stays hidden */ }
  }, [deal?.id, getAuthHeaders]);
  useEffect(() => { load(); }, [load, deal?.is_clawed_back, deal?.clawback_recovery_status]);

  if (!info || !info.has_commission) return null;
  const clawed = !!deal?.is_clawed_back;
  const status = deal?.clawback_recovery_status || (clawed ? 'requested' : null);
  const amount = deal?.clawback_amount ?? info.commission_amount;

  const post = async (path, body, ok) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/commissions/${path}`, body, { headers: getAuthHeaders() });
      toast.success(ok || r.data?.message || 'Saved');
      setDlg(null);
      if (onChanged) onChanged();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="clawback-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Undo2 className="h-4 w-4" /> Commission clawback
          {status && <Badge variant={BADGE[status] || 'outline'}>{status}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><p className="text-xs text-muted-foreground">Commission paid</p><p className="font-semibold">{formatCurrency(info.commission_amount)}</p><p className="text-[10px] text-muted-foreground">{info.commission_rate != null ? `${info.commission_rate}%` : ''}{info.broker_name ? ` · ${info.broker_name}` : ''}</p></div>
          <div><p className="text-xs text-muted-foreground">Funded → default</p><p className="font-semibold">{info.business_days_since_funding != null ? `${info.business_days_since_funding} business days` : '—'}</p><p className="text-[10px] text-muted-foreground">{info.within_window ? `inside the ${info.window_business_days}-day window` : `outside the ${info.window_business_days}-day window`}</p></div>
          {clawed && (
            <>
              <div><p className="text-xs text-muted-foreground">Requested</p><p className="font-semibold">{formatCurrency(amount)}</p><p className="text-[10px] text-muted-foreground">{deal.clawed_back_at ? formatDate(deal.clawed_back_at) : ''}</p></div>
              <div><p className="text-xs text-muted-foreground">{status === 'recovered' ? 'Recovered' : status === 'waived' ? 'Waived' : 'Outstanding'}</p>
                <p className="font-semibold">{status === 'recovered' ? formatCurrency(deal.clawback_recovered_amount) : status === 'waived' ? '—' : formatCurrency(amount)}</p>
                <p className="text-[10px] text-muted-foreground">{status === 'recovered' ? `${formatDate(deal.clawback_recovered_at)} ${deal.clawback_recovery_reference || ''}` : ''}</p></div>
            </>
          )}
        </div>
        {deal?.clawback_reason && <p className="text-xs text-muted-foreground">Reason: {deal.clawback_reason}</p>}
        {deal?.clawback_recovery_note && <p className="text-xs text-muted-foreground">Note: {deal.clawback_recovery_note}</p>}
        <div className="flex flex-wrap gap-2 pt-1">
          {!clawed && <Button size="sm" variant="outline" className="text-red-700" onClick={() => { setReason(info.within_window ? `Defaulted ${info.business_days_since_funding} business day(s) after funding` : ''); setDlg('claw'); }} data-testid="clawback-request-btn">Claw back commission</Button>}
          {clawed && status === 'requested' && (
            <>
              <Button size="sm" onClick={() => { setRec(r => ({ ...r, amount: String(amount ?? '') })); setDlg('recovered'); }} data-testid="clawback-recovered-btn">Mark recovered</Button>
              <Button size="sm" variant="outline" onClick={() => post('clawback/recovery', { deal_uuid: deal.id, status: 'waived' }, 'Clawback waived')} disabled={busy}>Waive</Button>
            </>
          )}
          {clawed && status !== 'requested' && <Button size="sm" variant="ghost" onClick={() => post('clawback/recovery', { deal_uuid: deal.id, status: 'requested' }, 'Clawback re-opened')} disabled={busy}>Re-open</Button>}
        </div>
      </CardContent>

      {dlg === 'claw' && (
        <Dialog open onOpenChange={(v) => !v && setDlg(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Claw back {formatCurrency(info.commission_amount)}{info.broker_name ? ` from ${info.broker_name}` : ''}?</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">Marks the commission as clawed back (syndicators who paid into it get a credit) and tracks the request until you mark the money recovered.</p>
            <Label className="text-xs">Reason</Label>
            <Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setDlg(null)} disabled={busy}>Cancel</Button>
              <Button variant="destructive" onClick={() => post('clawback', { deal_uuid: deal.id, reason }, 'Commission clawback requested')} disabled={busy}>Claw back</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {dlg === 'recovered' && (
        <Dialog open onOpenChange={(v) => !v && setDlg(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Clawback recovered</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date</Label><Input type="date" value={rec.date} onChange={e => setRec(r => ({ ...r, date: e.target.value }))} /></div>
              <div><Label className="text-xs">Amount</Label><Input type="number" step="0.01" value={rec.amount} onChange={e => setRec(r => ({ ...r, amount: e.target.value }))} /></div>
              <div className="col-span-2"><Label className="text-xs">Reference</Label><Input value={rec.reference} onChange={e => setRec(r => ({ ...r, reference: e.target.value }))} placeholder="wire / check / offset" /></div>
              <div className="col-span-2"><Label className="text-xs">Note</Label><Input value={rec.note} onChange={e => setRec(r => ({ ...r, note: e.target.value }))} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDlg(null)} disabled={busy}>Cancel</Button>
              <Button onClick={() => post('clawback/recovery', { deal_uuid: deal.id, status: 'recovered', date: rec.date, amount: parseFloat(rec.amount), reference: rec.reference, note: rec.note }, 'Recovery recorded')} disabled={busy}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

export default ClawbackCard;
